import React, { memo, MouseEvent, useCallback, useMemo, useState } from 'react';
import { ButtonComponent } from '../../../../ReusableComponents/Button/ButtonComponent';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import { RegionCapacity } from '../../../../types/capacityTypes';
import { FiEdit3 } from 'react-icons/fi';
import { useDeepCompareCallback, useDeepCompareEffect } from 'use-deep-compare';
import { capitalize } from '../../../../utils/strings';
import { EditRegionConfigModal } from './EditRegionCapacityModal/EditRegionConfigModal';
import ModalComponent from '../../../../ReusableComponents/Modal/ModalComponent';
import { Snackbar } from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import './CapacityRegionSection.scss';
import { Dayjs } from 'dayjs';
import { javaDayOfWeek } from '../../../../utils/date';
import { roundIfNeeded } from '../../../../utils/number';
import {
  associateByRegion,
  RegionOrderCount,
  RegionZoneIntervalOrderCount,
} from '../../../../types/statsTypes';
import { TooltipComponent } from '../../../../ReusableComponents/Tooltip/TooltipComponent';
import { init } from '@sentry/react';

interface ToastConfigInterface {
  text: string;
  type: 'success' | 'error';
}

interface Props {
  regionCapacity: RegionCapacity[];
  orderCountRegion: RegionZoneIntervalOrderCount[];
  ordersToSortCount: RegionOrderCount[];
  updateRegionalCapacity: () => void;
  allowEditing: boolean;
  deliveryDate: Dayjs;
}

interface RegionCountSummary {
  cachedDeliveryCount: number;
  cachedPrebookingsCount: number;
  deliveryCount: number;
  orderCount: number;
  predictedParcelCount: number;
}

const CapacityRegionSection = memo((props: Props) => {
  const {
    regionCapacity,
    orderCountRegion,
    ordersToSortCount,
    updateRegionalCapacity,
    allowEditing,
    deliveryDate,
  } = props;
  const [isToastMessageVisible, toggleToastMessage] = useState<boolean>(false);
  const [isLoaderVisible, toggleLoader] = useState<boolean>(false);
  const [toastConfigObject, setToastConfigObject] =
    useState<ToastConfigInterface>({ text: '', type: 'error' });
  const [selectedRegion, setSelectedRegion] = useState<
    RegionCapacity | undefined
  >(undefined);
  const [isEditRegionConfigModalVisible, toggleEditRegionModal] =
    useState<boolean>(false);

  const editIcon = useMemo((): JSX.Element => <FiEdit3 />, []);

  const regionName = (region: RegionCapacity): string => {
    return region.name
      .split('_')
      .map(str => capitalize(str))
      .join(' ');
  };

  const onCloseModalCallback = useCallback((): void => {
    toggleEditRegionModal(false);
    setSelectedRegion(undefined);
  }, []);

  const onCloseToast = useCallback((): void => toggleToastMessage(false), []);

  const triggerRegionEdit = useDeepCompareCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      const clickedRegion = e.currentTarget.value;
      const regionToEdit = regionCapacity.find(
        region => region.name === clickedRegion
      );
      setSelectedRegion(regionToEdit);
      toggleEditRegionModal(true);
    },
    [regionCapacity]
  );

  const getRegionDeliveryCap = (
    regionCapacity: RegionCapacity
  ): number | null => {
    const dayOfWeek = javaDayOfWeek(deliveryDate);
    const dayCapacity =
      regionCapacity.maxDeliveriesPerWeekDay !== null
        ? regionCapacity.maxDeliveriesPerWeekDay[dayOfWeek]
        : null;
    if (dayCapacity === undefined || dayCapacity === null) {
      return regionCapacity.maxDailyDeliveries;
    }
    return +dayCapacity;
  };

  useDeepCompareEffect(() => {
    if (regionCapacity) {
      setTimeout((): void => toggleLoader(false), 1000);
    }
  }, [regionCapacity]);

  const orderCountByRegion = associateByRegion(orderCountRegion);
  const ordersToSortCountByRegion = associateByRegion(ordersToSortCount);

  const sortingForLineHaulSum = regionCapacity
    .reduce((current: number, region: RegionCapacity) => {
      //Interim solution until we visualize different products
      const sortingForLineHaul =
        ordersToSortCountByRegion[region.name]?.orderCount['delivery'] || 0;
      return current + sortingForLineHaul;
    }, 0)
    .toString();

  const maxDailyDeliveriesSum = regionCapacity.reduce(
    (current: number, region: RegionCapacity) => {
      const regionDeliveryCap = getRegionDeliveryCap(region) || 0;
      return current + regionDeliveryCap;
    },
    0
  );

  const regionCountSummary = orderCountRegion.reduce<RegionCountSummary>(
    (summary, current) => {
      return {
        cachedDeliveryCount:
          summary.cachedDeliveryCount +
          (current.count['delivery']?.cachedDeliveryCount || 0),
        cachedPrebookingsCount:
          summary.cachedPrebookingsCount +
          (current.count['delivery']?.cachedPrebookingsCount || 0),
        deliveryCount:
          summary.deliveryCount +
          (current.count['delivery']?.deliveryCount || 0),
        orderCount:
          summary.orderCount + (current.count['delivery']?.orderCount || 0),
        predictedParcelCount:
          summary.predictedParcelCount + current.predictedParcelCount,
      };
    },
    {
      cachedDeliveryCount: 0,
      cachedPrebookingsCount: 0,
      deliveryCount: 0,
      orderCount: 0,
      predictedParcelCount: 0,
    }
  );

  return (
    <>
      <div className="capacity-info__region-container">
        <div className="capacity-info__region-header">
          <h6>Regional Capacity - Last Mile Daily</h6>
        </div>
        <TableContainer className="capacity-info__region-table" component="div">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Region</TableCell>
                {allowEditing && <TableCell align="center">Capacity</TableCell>}
                <TableCell align="center">
                  <TooltipComponent
                    id="bookings_tooltip"
                    title="Current cached number of booked deliveries (placed deliveries + IBX HD prebookings)"
                  >
                    Bookings
                  </TooltipComponent>
                </TableCell>
                <TableCell align="center">Deliveries</TableCell>
                <TableCell align="center">Orders</TableCell>
                <TableCell align="center">Orders to sort</TableCell>
                <TableCell align="center">Predicted parcels</TableCell>
                {allowEditing && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {regionCapacity.map((region: RegionCapacity) => {
                if (isLoaderVisible) {
                  return (
                    <TableRow key={region.name}>
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                  );
                }
                const regionDeliveryCap = getRegionDeliveryCap(region);

                //Interim solution until we visualize different products
                const regionCount =
                  orderCountByRegion[region.name]?.count['delivery'];
                //Interim solution until we visualize different products
                const deliveryCountToSort =
                  ordersToSortCountByRegion[region.name]?.orderCount[
                    'delivery'
                  ] || 0;

                return (
                  <TableRow key={region.name}>
                    <TableCell>{regionName(region)}</TableCell>
                    {allowEditing && (
                      <TableCell align="center">
                        <span>
                          {regionDeliveryCap !== null ? regionDeliveryCap : '-'}
                        </span>
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <TooltipComponent
                        id={`booking-count-${region.name}`}
                        title={`Includes ${
                          regionCount?.cachedPrebookingsCount || 0
                        } IBX HD prebookings`}
                      >
                        {regionCount?.cachedDeliveryCount || 0}
                      </TooltipComponent>
                    </TableCell>
                    <TableCell align="center">
                      {regionCount?.deliveryCount || 0}
                    </TableCell>
                    <TableCell align="center">
                      {regionCount?.orderCount || 0}
                    </TableCell>
                    <TableCell align="center">{deliveryCountToSort}</TableCell>
                    <TableCell align="center">
                      {roundIfNeeded(
                        orderCountByRegion[region.name]?.predictedParcelCount
                      ) || 0}
                    </TableCell>
                    {allowEditing && (
                      <TableCell align="center">
                        <ButtonComponent
                          startIcon={editIcon}
                          text="Edit"
                          variant="text"
                          value={region.name}
                          onClick={triggerRegionEdit}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell>Summary</TableCell>
                {allowEditing && (
                  <TableCell align="center">{maxDailyDeliveriesSum}</TableCell>
                )}
                <TableCell align="center">
                  <TooltipComponent
                    id="booking-count-summary"
                    title={`Includes ${
                      regionCountSummary.cachedPrebookingsCount || 0
                    } IBX HD prebookings`}
                  >
                    {regionCountSummary.cachedDeliveryCount}
                  </TooltipComponent>
                </TableCell>
                <TableCell align="center">
                  {regionCountSummary.deliveryCount}
                </TableCell>
                <TableCell align="center">
                  {regionCountSummary.orderCount}
                </TableCell>
                <TableCell align="center">{sortingForLineHaulSum}</TableCell>
                <TableCell align="center">
                  {roundIfNeeded(regionCountSummary.predictedParcelCount)}
                </TableCell>
                {allowEditing && <TableCell />}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={isToastMessageVisible}
        autoHideDuration={3000}
        onClose={onCloseToast}
      >
        <Alert onClose={onCloseToast} severity={toastConfigObject.type}>
          {toastConfigObject.text}
        </Alert>
      </Snackbar>
      {selectedRegion && (
        <ModalComponent
          showCloseButton={true}
          className="edit-region-config-modal"
          open={isEditRegionConfigModalVisible}
          setOpen={toggleEditRegionModal}
          title={`Edit capacity for ${capitalize(selectedRegion?.name || '')}`}
        >
          <EditRegionConfigModal
            updateRegionalCapacity={updateRegionalCapacity}
            setToastConfigObject={setToastConfigObject}
            toggleToastMessage={toggleToastMessage}
            regionName={selectedRegion.name}
            maxDailyCapacity={selectedRegion.maxDailyDeliveries}
            maxDeliveriesPerWeekDay={selectedRegion.maxDeliveriesPerWeekDay}
            onCloseModal={onCloseModalCallback}
            toggleLoader={toggleLoader}
          />
        </ModalComponent>
      )}
    </>
  );
});

CapacityRegionSection.displayName = 'CapacityRegionSection';
CapacityRegionSection.whyDidYouRender = true;
export { CapacityRegionSection };
